import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompanyData, CompanyStatus } from "@/pages/companies/types";
import { useActiveCompanyContracts } from "./hooks";
import Text from "@/components/ui/text";
import CommercialsEmptyState from "@/pages/companies/components/CommercialsEmptyState";
import CompanyFinancialsSection from "./components/CompanyFinancialsSection";
import Spinner from "@/components/ui/spinner";
import DealContractCard from "@/pages/companies/components/CompanyCommercialsV2/components/DealContractCard";

const TRANSLATION_PREFIX = "companies.commercials";

interface Props {
  companyData: CompanyData;
}

const CompanyCommercialsV2: React.FC<Props> = ({ companyData }) => {
  const { t } = useTranslation();
  const { contracts, isLoading, error } = useActiveCompanyContracts(
    companyData?.id
  );

  // Get currency from the first contract for display purposes
  const contractCurrency = useMemo(() => {
    if (contracts.length === 0) return null;
    return contracts[0]?.currency ?? null;
  }, [contracts]);

  if (companyData?.status !== CompanyStatus.ACTIVE_CUSTOMER) {
    let title = `${TRANSLATION_PREFIX}.empty.title`;
    let subtitle = `${TRANSLATION_PREFIX}.empty.subtitle`;
    if (companyData?.status === CompanyStatus.CHURNED) {
      title = `${TRANSLATION_PREFIX}.empty.churned.title`;
      subtitle = `${TRANSLATION_PREFIX}.empty.churned.subtitle`;
    }
    return (
      <CommercialsEmptyState
        companyName={companyData?.name}
        title={title}
        subtitle={subtitle}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 text-center mt-[7rem]">
        <Text variant="lgLabel">{t("common.error")}</Text>
        <Text variant="bodySecondary">
          {t("companies.commercials.errorLoading")}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 w-full max-w-[1024px] p-6">
      <CompanyFinancialsSection
        companyData={companyData}
        contractCurrency={contractCurrency}
      />
      <div className="flex flex-col gap-4">
        <Text variant="smSubtitle">
          {t(`${TRANSLATION_PREFIX}.wonDealsTermsServices`)}
        </Text>
        <div className="flex flex-col gap-4 w-full">
          {contracts.length === 0 ? (
            <div className="flex flex-col gap-2 p-6 bg-surface-widget-card rounded-xl border border-border-widget-card text-center">
              <Text variant="lgLabel">
                {t(`${TRANSLATION_PREFIX}.noActiveContracts`)}
              </Text>
              <Text variant="bodySecondary">
                {t(`${TRANSLATION_PREFIX}.noActiveContractsSubtitle`)}
              </Text>
            </div>
          ) : (
            contracts.map((contract) => (
              <DealContractCard key={contract.id} contract={contract} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyCommercialsV2;
